(() => {
  if (customElements.get('ardg-flight-chart')) return;
  const NS = 'http://www.w3.org/2000/svg';
  function valid(d) {
    return d && ['speed','glide','turn','fade'].every(k => d[k] !== null && d[k] !== '' && Number.isFinite(Number(d[k]))) && Number(d.speed) >= 1 && Number(d.speed) <= 15 && Number(d.glide) >= 0 && Number(d.glide) <= 7 && Number(d.turn) >= -5 && Number(d.turn) <= 2 && Number(d.fade) >= 0 && Number(d.fade) <= 6;
  }
  function category(d) {
    const type = String(d.type || '').toLowerCase();
    if (/putt|approach/.test(type)) return 'putter';
    if (/mid/.test(type)) return 'mid';
    if (/driver/.test(type)) return 'driver';
    // Many Shopify products have generic types ("SHOP", "Disc Golf Discs").
    // Low-glide speed-4 molds such as Zone, Blast, Pig and Toro are approaches.
    return Number(d.speed) <= 3 || (Number(d.speed) === 4 && Number(d.glide) <= 3) ||
      (Number(d.speed) === 4 && /\bzone(?:\s+(?:ss|os|gt))?\b/i.test(String(d.title || '')))
      ? 'putter' : Number(d.speed) <= 5 ? 'mid' : 'driver';
  }
  const normalize = value => String(value || '').normalize('NFD').replace(/[\u0300-\u036f]/g,'').toLowerCase().replace(/[^a-z0-9]+/g,' ').trim();
  const clamp = (n,lo,hi) => Math.max(lo,Math.min(hi,n));
  function reference(d) {
    const bank=globalThis.ARDG_FLIGHT_DATA;
    const id=String(d.id).split('/').pop();
    const cached=bank?.products?.[id];
    if(cached && normalize(d.vendor)===normalize(cached.brand) && normalize(d.title)===cached.title && cached.printed.every((n,i)=>n===Number(d[['speed','glide','turn','fade'][i]]))) return cached;
    const title=` ${normalize(d.title || d.handle)} `;
    const printed=['speed','glide','turn','fade'].map(k=>Number(d[k]));
    const matched=(bank?.profiles || []).filter(r => normalize(r.brand)===normalize(d.vendor) && !(r.mold==='luna' && /\bultra luna\b/.test(title)) && Math.abs(r.printed[0]-printed[0])<=1 && r.printed.slice(1).every((n,i)=>Math.abs(n-printed[i+1])<=1.5) && [r.mold,...(r.aliases || [])].some(n => title.includes(` ${normalize(n)} `))).sort((a,b) => b.mold.length-a.mold.length)[0];
    if(!matched) return null;
    return {...matched,effective:matched.effective.map((n,i)=>i===0 ? printed[0] : printed[i]+clamp(n-matched.printed[i],-.75,.75))};
  }
  function baseline(d, distance = 300, style = 'backhand') {
    const ref=reference(d);
    const printed=['speed','glide','turn','fade'].map(k => Number(d[k]));
    const ratings=ref?.effective || printed;
    const [speed,glide,turn,fade]=ratings;
    const p=globalThis.ARDG_FLIGHT_DATA?.model || [.0459,.04372,.06543,.05376,.0589,.06249,1.66275,9.29304,.8];
    const kind=speed<=3 ? 0 : speed<=5 ? 1 : 2;
    const ratedDistance=kind===0 ? 250 : kind===1 ? 325 : clamp(190+22*speed,340,500);
    // The slider describes effort for this throw, expressed as a familiar distance.
    // It is not the player's maximum drive or a guaranteed landing distance.
    const power=clamp(distance/ratedDistance,.3,2.4);
    // A fast driver thrown well below its reference power cannot realize its
    // rated turn, even if the printed turn/fade numbers are very understable.
    const underpowered=kind===2 ? clamp((.70-power)/.22,0,1)*clamp((speed-8)/3,0,1) : 0;
    const turnGain=clamp(1+[.22,.36,.68][kind]*(power-1),.42,1.36)*(1-underpowered);
    // Forehand defaults to a clean release with a modestly lower spin/airspeed
    // ratio. This changes both turn and fade response, without assuming wobble.
    const forehand=style==='forehand';
    const precession=forehand ? 1.09 : 1;
    // Slower overstable throws devote more of their shorter flight to fade.
    // Scale the plotted fade displacement against effort instead of letting
    // sideways feet grow automatically with forward distance. This is a
    // shape heuristic, not a claim that low speed increases pitching torque.
    const finish=Math.max(Math.max(0,fade),1.7*underpowered);
    const fadeSensitivity=1.5*clamp((finish-1)/2,0,1);
    const fadeGain=clamp(Math.pow(power,-fadeSensitivity),.5,2);
    // Continuous curves avoid a hard straight segment or an onset seam.
    // Less power and more fade make the leftward bend build sooner.
    const fadeExponent=clamp(p[7]*Math.pow(power,1.6)/(1+.25*Math.max(0,fade-1))*(1-.2*underpowered)*(forehand?.95:1),1.6,32);
    return Array.from({length:101},(_,i) => {
      const t=i/100;
      return {x:-turn*turnGain*precession*p[kind*2]*Math.pow(t,p[6])-Math.pow(finish,p[8])*fadeGain*precession*p[kind*2+1]*Math.pow(t,fadeExponent),y:t};
    });
  }
  function flight(d, distance, hand, style) {
    const ratings=reference(d)?.effective || ['speed','glide','turn','fade'].map(k=>Number(d[k]));
    const group=category(d);
    const speed=Number(d.speed);
    const ratedDistance=group==='putter' ? 250 : group==='mid' ? 325 : clamp(190+22*speed,340,500);
    const shortfall=Math.max(0,1-distance/ratedDistance);
    const efficiency=(group==='putter' ? .98 : group==='mid' ? .97 : .97)
      - (group==='driver' ? .23 : group==='mid' ? .09 : .025)*shortfall
      + .035*(ratings[1]-(group==='putter' ? 3 : 5))
      + .009*Math.max(0,-ratings[2]-1)
      - .018*Math.max(0,ratings[3]-2);
    // The familiar 300/400/500 ft class ranges guide the starting view only.
    // A capable throw can carry a putter beyond 300 ft or a mid beyond 400 ft.
    const length=Math.min(distance+30,distance*clamp(efficiency,.62,1.12));
    const mirror=(hand==='left' ? -1 : 1)*(style==='forehand' ? -1 : 1);
    return baseline(d,distance,style).map(p=>({x:p.x*length*mirror,y:p.y*length}));
  }
  function el(tag, text, cls) {
    const node = document.createElement(tag);
    if (text !== undefined) node.textContent = text;
    if (cls) node.className = cls;
    return node;
  }
  function svg(tag, attrs, text) {
    const node = document.createElementNS(NS, tag);
    Object.entries(attrs || {}).forEach(([k,v]) => node.setAttribute(k,String(v)));
    if (text !== undefined) node.textContent = text;
    return node;
  }
  class FlightChart extends HTMLElement {
    connectedCallback() {
      this.listeners?.abort();
      this.listeners = new AbortController();
      this.labels = JSON.parse(this.querySelector('[data-labels]').textContent);
      this.current = JSON.parse(this.querySelector('[data-current]').textContent);
      this.discs = valid(this.current) ? [this.current] : [];
      this.results = new Map(); this.version = 0; this.next = null;
      this.limit = Number(this.dataset.limit || 4);
      const on = (node, event, fn) => node.addEventListener(event, fn, {signal:this.listeners.signal});
      ['[data-hand]','[data-throw]','[data-power]'].forEach(s => on(this.querySelector(s),'input',() => this.draw()));
      on(this.querySelector('[data-search]'),'input',() => {
        clearTimeout(this.timer); this.request?.abort(); this.version++;
        this.querySelector('[data-more]').hidden = true;
        this.querySelector('[data-results]').replaceChildren();
        this.results.clear(); this.next = null;
        const q = this.querySelector('[data-search]').value.trim();
        this.status('');
        if (q.length >= 2) this.timer = setTimeout(() => this.search(q),250);
      });
      on(this.querySelector('[data-more]'),'click',() => { if (this.next) this.search(this.querySelector('[data-search]').value.trim(),this.next); });
      this.draw();
    }
    disconnectedCallback() { clearTimeout(this.timer); this.request?.abort(); this.listeners?.abort(); }
    status(text) { this.querySelector('[data-status]').textContent = text; }
    color(d) {
      if (String(d.id) === String(this.current.id)) return getComputedStyle(this).getPropertyValue('--flight-accent').trim() || '#176b65';
      const index = this.discs.filter(x => String(x.id) !== String(this.current.id)).findIndex(x => String(x.id) === String(d.id));
      return ['#b65320','#6253b6','#a63165','#2574a8','#617422'][Math.max(0,index)];
    }
    draw() {
      const arm = Number(this.querySelector('[data-power]').value);
      this.querySelector('[data-power-label]').textContent = `${arm} ${this.labels.feet}`;
      const hand = this.querySelector('[data-hand]').value, style = this.querySelector('[data-throw]').value;
      const container = this.querySelector('[data-charts]'); container.replaceChildren();
      if (!this.discs.length) container.append(el('p',this.labels.missing));
      const plotted = this.discs;
      if (plotted.length) {
        const discs = plotted;
        const groups = new Set(discs.map(category));
        const group = groups.has('driver') ? 'driver' : groups.has('mid') ? 'mid' : 'putter';
        const chartLabel = groups.size > 1 ? this.labels.comparison : this.labels[group];
        const cap = arm + 30;
        const plotHeight = 250;
        // A separate labeled lateral scale keeps real sideways movement legible.
        // It depends only on the slider, so comparison discs never resize axes.
        const side = Math.max(85,arm * .58);
        const plotX = feet => 190 + feet * 150 / side;
        const plotY = feet => plotHeight + 24 - feet * plotHeight / cap;
        const panel = el('div',undefined,'afc-panel');
        const heading = el('h3',chartLabel); heading.append(el('span',`${cap} ${this.labels.feet}`)); panel.append(heading);
        const chart = svg('svg',{viewBox:`0 0 380 ${plotHeight+64}`,role:'img','aria-label':`${chartLabel} ${this.labels.chart}`});
        chart.append(svg('title',{},`${chartLabel} — ${this.labels.chart}`));
        const desc = svg('desc'); const descriptions = [];
        chart.append(desc);
        const uid = `afc-${this.querySelector('input[type=search]').id}-${group}`;
        const defs = svg('defs'); const clip = svg('clipPath',{id:uid}); clip.append(svg('rect',{x:40,y:24,width:300,height:plotHeight})); defs.append(clip); chart.append(defs);
        const ticks = [];
        // Always mark the chart limit, but omit the preceding 50-ft mark when
        // its label would be less than 22 SVG pixels from the limit label.
        for (let feet = 0; feet < cap; feet += 50) {
          if ((cap-feet)*plotHeight/cap >= 22) ticks.push(feet);
        }
        ticks.push(cap);
        for (const feet of ticks) {
          const y = plotY(feet);
          chart.append(svg('line',{x1:40,x2:340,y1:y,y2:y,class:'afc-grid'}));
          chart.append(svg('text',{x:32,y:y+4,'text-anchor':'end'},feet));
        }
        const lateralStep = side <= 100 ? 25 : side <= 210 ? 50 : 100;
        const offsets = [-side];
        for (let offset = Math.ceil(-side / lateralStep) * lateralStep; offset < side; offset += lateralStep) {
          if (offset > -side) offsets.push(offset);
        }
        offsets.push(side);
        for (const offset of offsets) {
          const x = plotX(offset);
          chart.append(svg('line',{x1:x,x2:x,y1:24,y2:plotHeight+24,class:offset === 0 ? 'afc-center':'afc-grid'}));
          if (offset !== -side && offset !== side) chart.append(svg('text',{x,y:plotHeight+43,'text-anchor':'middle'},offset === 0 ? this.labels.tee : offset));
        }
        let overflow = false;
        discs.forEach(d => {
          const points = flight(d,arm,hand,style);
          const last = points[points.length-1];
          const out = points.some(p => p.y > cap || Math.abs(p.x) > side);
          overflow ||= out;
          descriptions.push(`${d.title}: ${this.labels.typical}`);
          const path = svg('path',{d:points.map((p,i) => `${i?'L':'M'}${plotX(p.x).toFixed(2)},${plotY(p.y).toFixed(2)}`).join(' '),stroke:this.color(d),class:'afc-line','clip-path':`url(#${uid})`});
          if (String(d.id) !== String(this.current.id)) path.setAttribute('stroke-dasharray','8 4');
          path.append(svg('title',{},`${d.title} — ${this.labels.typical}`)); chart.append(path);
          if (!out) chart.append(svg('circle',{cx:plotX(last.x),cy:plotY(last.y),r:4,fill:this.color(d)}));
        });
        desc.textContent = descriptions.join('. ');
        panel.append(chart);
        panel.append(el('p',this.labels.distance_note,'afc-distance-note'));
        if (overflow) panel.append(el('p',this.labels.overflow));
        container.append(panel);
      }
      const legend = this.querySelector('[data-legend]'); legend.replaceChildren();
      this.discs.forEach(d => {
        const row = el('li');
        const swatch = el('span',undefined,'afc-swatch'); swatch.style.background = this.color(d); swatch.setAttribute('aria-hidden','true');
        const text = el('span'); const link = el('a',d.title); link.href = d.url;
        text.append(link,el('small',`${d.speed} / ${d.glide} / ${d.turn} / ${d.fade}`)); row.append(swatch,text);
        if (String(d.id) !== String(this.current.id)) {
          const remove = el('button','×','afc-remove'); remove.type = 'button'; remove.setAttribute('aria-label',`${this.labels.remove} ${d.title}`);
          remove.addEventListener('click',() => {
            this.discs = this.discs.filter(x => String(x.id) !== String(d.id)); this.draw(); this.renderResults(); this.status('');
            this.querySelector('[data-search]').focus();
          }); row.append(remove);
        }
        legend.append(row);
      });
    }
    async search(q, next) {
      this.request?.abort(); this.request = new AbortController(); const version = ++this.version;
      const more = this.querySelector('[data-more]'); more.disabled = true;
      this.status(this.labels.searching);
      const url = new URL(next || this.dataset.searchUrl,location.origin);
      if (url.origin !== location.origin) { this.status(this.labels.error); return; }
      url.searchParams.set('q',q); url.searchParams.set('type','product'); url.searchParams.set('options[prefix]','last'); url.searchParams.set('section_id','ardg-flight-search');
      if (!next) url.searchParams.delete('page');
      try {
        const response = await fetch(url,{signal:this.request.signal});
        if (!response.ok) throw new Error('Search failed');
        const doc = new DOMParser().parseFromString(await response.text(),'text/html');
        const data = doc.querySelector('[data-flight-search]'); if (!data) throw new Error('Missing search section');
        if (version !== this.version || !this.isConnected) return;
        if (!next) this.results.clear();
        data.querySelectorAll('[data-disc]').forEach(node => {
          const d = JSON.parse(node.textContent);
          if (valid(d) && (this.dataset.stockOnly !== 'true' || d.available)) this.results.set(String(d.id),d);
        });
        this.next = data.dataset.next || null;
        more.hidden = !this.next; this.renderResults();
        this.status(this.results.size ? '' : this.labels.empty);
      } catch (e) {
        if (e.name !== 'AbortError' && version === this.version) this.status(this.labels.error);
      } finally { if (version === this.version) more.disabled = false; }
    }
    renderResults() {
      const list = this.querySelector('[data-results]'); list.replaceChildren();
      this.results.forEach(d => {
        const added = this.discs.some(x => String(x.id) === String(d.id));
        const full = this.discs.length >= this.limit;
        const row = el('li'); const button = el('button'); button.type = 'button'; button.disabled = added || full;
        const title = el('span',d.title); title.append(el('small',`${d.speed} / ${d.glide} / ${d.turn} / ${d.fade}${d.available?'':` · ${this.labels.sold_out}`}`));
        button.append(title,el('span',added ? this.labels.added : this.labels.add,'afc-add'));
        button.addEventListener('click',() => {
          if (this.discs.length >= this.limit) return;
          this.discs.push(d); this.draw(); this.renderResults();
          this.status(this.discs.length >= this.limit ? this.labels.limit : `${this.labels.added}: ${d.title}`);
          this.querySelector('[data-search]').focus();
        }); row.append(button); list.append(row);
      });
    }
  }
  customElements.define('ardg-flight-chart',FlightChart);
})();
